/**
 * Console capture utility to store recent console logs for debugging
 */

export interface ConsoleLog {
  timestamp: Date;
  level: 'log' | 'error' | 'warn' | 'info';
  args: any[];
  stack?: string;
}

class ConsoleCapture {
  private logs: ConsoleLog[] = [];
  private maxLogs = 100; // Keep last 100 logs
  private originalConsole = {
    log: console.log,
    error: console.error,
    warn: console.warn,
    info: console.info,
  };

  constructor() {
    this.interceptConsole();
  }

  private interceptConsole() {
    const capture = (level: ConsoleLog['level']) => {
      return (...args: any[]) => {
        // Store the log
        this.logs.push({
          timestamp: new Date(),
          level,
          args,
          stack: level === 'error' ? new Error().stack : undefined,
        });

        // Keep only the last maxLogs entries
        if (this.logs.length > this.maxLogs) {
          this.logs.shift();
        }

        // Call original console method
        this.originalConsole[level](...args);
      };
    };

    console.log = capture('log');
    console.error = capture('error');
    console.warn = capture('warn');
    console.info = capture('info');
  }

  public getLogs(): ConsoleLog[] {
    return [...this.logs];
  }

  public getRecentLogs(count: number = 50): ConsoleLog[] {
    return this.logs.slice(-count);
  }

  public clear() {
    this.logs = [];
  }

  public formatLogsAsText(logs?: ConsoleLog[]): string {
    const logsToFormat = logs || this.logs;
    return logsToFormat
      .map((log) => {
        const timestamp = log.timestamp.toISOString();
        const level = log.level.toUpperCase().padEnd(5);
        const message = log.args
          .map((arg) => {
            if (typeof arg === 'object') {
              try {
                return JSON.stringify(arg, null, 2);
              } catch {
                return String(arg);
              }
            }
            return String(arg);
          })
          .join(' ');
        return `[${timestamp}] ${level} ${message}`;
      })
      .join('\n');
  }
}

// Create singleton instance
export const consoleCapture = new ConsoleCapture();
